import React, { useState, useEffect } from "react";
import { ShowButton, EditButton } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import { Table, Tag, Space, Card, Input } from "antd";
import { SearchOutlined, BankOutlined } from "@ant-design/icons";
import { PageHeader } from "../../components/ui";

interface Company {
    id: number;
    name: string;
    tradeName: string;
    vatNumber: string;
    tpin: string;
    isActive: boolean;
    createdAt: string;
}

export const CompanyList: React.FC = () => {
    const [searchText, setSearchText] = useState<string>("");
    const [filteredItems, setFilteredItems] = useState<Company[]>([]);

    const { data, isLoading } = useList({
        resource: "company",
    });

    useEffect(() => {
        if (!data?.data) return;

        // Map nested response if needed
        let companies = (data.data as any[]).map((item: any) => item.value || item) as Company[];

        if (searchText.trim() !== "") {
            const searchLower = searchText.toLowerCase();
            companies = companies.filter((company: Company) =>
                company.name?.toLowerCase().includes(searchLower) ||
                company.tradeName?.toLowerCase().includes(searchLower) ||
                company.tpin?.toLowerCase().includes(searchLower)
            );
        }

        const sorted = [...companies].sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setFilteredItems(sorted);
    }, [data, searchText]);

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (value: string) => (
                <span className="font-medium text-text-primary">{value}</span>
            ),
        },
        {
            title: "Trade Name",
            dataIndex: "tradeName",
            key: "tradeName",
            render: (value: string) => (
                <span className="text-text-secondary">{value || "-"}</span>
            ),
            responsive: ['md'] as any,
        },
        {
            title: "VAT Number",
            dataIndex: "vatNumber",
            key: "vatNumber",
            render: (value: string) => (
                <span className="text-text-secondary">{value || "-"}</span>
            ),
            responsive: ['lg'] as any,
        },
        {
            title: "TPIN",
            dataIndex: "tpin",
            key: "tpin",
            render: (value: string) => (
                <span className="text-text-secondary">{value || "-"}</span>
            ),
        },
        {
            title: "Status",
            dataIndex: "isActive",
            key: "status",
            render: (value: boolean) => (
                <Tag color={value ? "success" : "default"} className="status-tag">
                    {value ? "Active" : "Inactive"}
                </Tag>
            ),
        },
        {
            title: "Created",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString() : "-"}
                </span>
            ),
            responsive: ['md'] as any,
        },
        {
            title: "Actions",
            key: "actions",
            width: 100,
            render: (_: any, record: Company) => (
                <Space size="small">
                    <EditButton hideText size="small" recordItemId={record.id} />
                    <ShowButton hideText size="small" recordItemId={record.id} />
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Company"
                icon={<BankOutlined />}
                breadcrumbs={[{ title: "Company" }]}
            />

            <div className="data-table-card">
                <Card>
                    <div style={{ marginBottom: 16 }}>
                        <Input
                            placeholder="Search by name, trade name, or TPIN"
                            prefix={<SearchOutlined className="text-text-muted" />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ maxWidth: 400 }}
                            allowClear
                            size="large"
                        />
                    </div>

                    <Table
                        dataSource={filteredItems}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        }}
                        className="custom-table"
                        scroll={{ x: 800 }}
                    />
                </Card>
            </div>
        </div>
    );
};
